import React from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaComment } from 'react-icons/fa'; // Added FaComment for the form

// Using the primary green color for theme consistency
const PRIMARY_COLOR = '#4f6a39';

// Placeholder URLs
const GOOGLE_MAPS_EMBED_URL = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d113645.74818788931!2d79.37893902371971!3d28.384666579040954!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a007137df7d31d%3A0x6d9f80905e9c063c!2sBareilly%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1689758782000!5m2!1sen!2sin"; 
const BANNER_IMAGE = "https://www.pandiyarseemai.in/jb-content/uploads/2022/09/inner-banner-1-1.jpg";

const ContactPage: React.FC = () => {
    
    // Placeholder Contact Information
    const contactInfo = {
        address: "Village & Post: Prem Nagar, Bareilly, Uttar Pradesh - 243001, India",
        phone: "+91 6395178971",
        email: "info@bereillyvermicompost.com",
        hours: "Mon - Sat: 9:00 AM - 6:00 PM (IST)",
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        alert("Thank you for your message! We will get back to you shortly.");
        // In a real application, you would handle form submission here (e.g., using fetch or a serverless function)
    };

    return (
        <div className="bg-gray-50 min-h-screen mt-32">
            
            {/* 1. Header/Banner - Responsive Height and Text Size */}
            <div 
                // Adjusted height for better mobile look: h-64 -> sm:h-80
                className="relative bg-cover bg-center h-64 sm:h-80 flex items-center justify-center px-4" 
                style={{ backgroundImage: `url(${BANNER_IMAGE})` }}
            >
                <div className="absolute inset-0 bg-black opacity-60"></div>
                
                {/* Scaled text: 4xl -> 5xl */}
                <h1 className="relative text-4xl sm:text-5xl font-extrabold text-white z-10 uppercase tracking-wide font-serif">
                    Contact Us
                </h1>
            </div>

            {/* 2. Main Content Area (Form & Details) - Responsive Stacking and Padding */}
            <div className="py-12 sm:py-20 px-4 sm:px-8">
                <div className="max-w-7xl mx-auto">

                    {/* Intro Text */}
                    <div className="text-center mb-10 sm:mb-16">
                        <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                            We are ready to answer your questions about vermicompost, bulk orders, and organic farming. Reach out to our team today!
                        </p>
                    </div>

                    {/* Main Flex Container: Form Left, Details Right */}
                    <div className="flex flex-col md:flex-row gap-10 md:gap-16">

                        {/* Left: Contact Form - Full width on mobile, half on desktop */}
                        <div className="w-full md:w-1/2 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-[#4f6a39]">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 flex items-center">
                                <FaComment className="mr-3 text-xl" style={{ color: PRIMARY_COLOR }} />
                                Send Us a Message
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                
                                {/* Name and Email Fields (Side-by-side on tablet/desktop, stacked on mobile) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            required
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4f6a39] focus:border-[#4f6a39] sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4f6a39] focus:border-[#4f6a39] sm:text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Subject Field (Full width) */}
                                <div>
                                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Subject / Enquiry Type</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4f6a39] focus:border-[#4f6a39] sm:text-sm"
                                    />
                                </div>
                                
                                {/* Message Field (Full width) */}
                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700">Your Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        required
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4f6a39] focus:border-[#4f6a39] sm:text-sm"
                                    ></textarea>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="w-full justify-center inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#4f6a39] hover:bg-green-700 transition duration-150 mt-4"
                                >
                                    Submit Enquiry
                                </button>
                            </form>
                        </div>

                        {/* Right: Contact Details - Full width on mobile, half on desktop */}
                        <div className="w-full md:w-1/2 bg-white p-6 sm:p-8 rounded-xl shadow-lg">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Our Contact Information</h2>

                            <div className="space-y-6">
                                {/* Address */}
                                <div className="flex items-start space-x-4">
                                    <FaMapMarkerAlt className="text-2xl text-[#4f6a39] mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Location</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">{contactInfo.address}</p>
                                    </div>
                                </div>

                                {/* Phone */}
                                <div className="flex items-start space-x-4">
                                    <FaPhone className="text-2xl text-[#4f6a39] mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Call Us</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">{contactInfo.phone}</p>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="flex items-start space-x-4">
                                    <FaEnvelope className="text-2xl text-[#4f6a39] mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Email</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">{contactInfo.email}</p>
                                    </div>
                                </div>

                                {/* Hours */}
                                <div className="flex items-start space-x-4">
                                    <FaClock className="text-2xl text-[#4f6a39] mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Working Hours</h3>
                                        <p className="text-gray-600 text-sm sm:text-base">{contactInfo.hours}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Map Embed - Full width */}
                    <div className="w-full mt-10 sm:mt-16">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">Find Us on Map</h2>
                        <iframe
                            src={GOOGLE_MAPS_EMBED_URL}
                            width="100%"
                            height="350"
                            style={{ border: 0 }}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Bereilly Vermi Compost Location"
                            className="rounded-lg shadow-xl"
                        ></iframe>
                    </div>
                        
                </div>
            </div>
        </div>
    );
};

export default ContactPage;